import React, { useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Turnstile } from '@marsidev/react-turnstile';

import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import * as validators from '../../util/validators';
import { createResourceLocatorString } from '../../util/routes';
import { types as sdkTypes } from '../../util/sdkLoader';
import { buildPickupLocationUpdate } from '../../util/listingLocation';
import {
  Page,
  LayoutSingleColumn,
  Form,
  FieldSelect,
  FieldTextInput,
  FieldCheckboxGroup,
  FieldLocationAutocompleteInput,
  ImageFromFile,
  Heading,
  NamedLink,
  PrimaryButton,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { startGuestPost, EMAIL_EXISTS_CODE } from './GuestQuotePage.duck';
import css from './GuestQuotePage.module.css';

const { LatLng } = sdkTypes;

// Cloudflare Turnstile's public "always passes" TEST sitekey — pairs with the TEST secret
// server-side. Used for local/dev + CI until a real REACT_APP_TURNSTILE_SITEKEY is provisioned.
// See server/api-util/turnstile.js (which fails closed until TURNSTILE_SECRET is set in prod).
const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA';

// What this picker accepts. Owned here rather than imported from the AI-estimate module: photos are
// now the ONLY description of a job (the free-text description field was removed), so the limits
// belong to posting, not to a feature that is switched off. The old cap of 6 was the estimate
// endpoint's vision-payload limit, which no longer applies.
//
// ⚠️ These are SHARETRIBE's limits, not ours, and they are narrower than the AI scope's are:
// "Images can only be uploaded as JPG, PNG, or non-animated GIF" — anything else fails the upload
// with a 400 `image-invalid-content`. WebP was accepted here while these constants were shared with
// the vision API, which sends base64 to Anthropic and never touches Sharetribe. That matters more on
// this page than anywhere else: the upload happens AFTER /api/guest-start has already created the
// account and signed the visitor in, so an unsupported photo would leave them holding an account
// they never asked for and no job. Reject it at the picker instead.
//
// GIF is excluded too, even though Sharetribe allows the non-animated kind: a MIME type cannot tell
// the two apart, so accepting `image/gif` would let an animated one through to the same stranded-
// account failure. Nobody photographs a pile of junk as a GIF; JPEG and PNG cover every camera and
// screenshot path.
const MAX_PHOTOS = 10;
const ALLOWED_MEDIA_TYPES = ['image/jpeg', 'image/png'];
const FILE_INPUT_ACCEPT = ALLOWED_MEDIA_TYPES.join(',');
// Sharetribe's own ceiling is 20 MB per image (`request-upload-over-limit` above it). The old 5 MB
// bound came from the AI scope's base64 payload, not from the upload — and now that a photo is
// REQUIRED to post, an over-tight cap doesn't degrade the experience, it blocks the job entirely.
// Modern phone cameras routinely produce 5-12 MB files.
const MAX_PHOTO_MB = 20;
const MAX_PHOTO_BYTES = MAX_PHOTO_MB * 1024 * 1024;

// Option lists mirror the listing config (src/config/configListing.js — serviceType / junkType /
// loadSize). Kept local so the guest quote has stable options regardless of hosted-config merge,
// and reads as { key, label } for FieldSelect/FieldCheckboxGroup. Source of truth for a job's real
// fields stays configListing.js; these three are collected here to seed the Phase-2 post-a-job step.
const SERVICE_TYPE_OPTIONS = [
  { key: 'junk-removal', label: 'Junk removal' },
  { key: 'logistics', label: 'Logistics & moving' },
  { key: 'storage-cleanout', label: 'Storage unit clean-out' },
];
const JUNK_TYPE_OPTIONS = [
  { key: 'furniture', label: 'Furniture' },
  { key: 'appliances', label: 'Appliances' },
  { key: 'mattress', label: 'Mattress / box spring' },
  { key: 'electronics', label: 'Electronics / e-waste' },
  { key: 'yard-waste', label: 'Yard waste' },
  { key: 'construction-debris', label: 'Construction debris' },
  { key: 'household-junk', label: 'General household junk' },
  { key: 'hot-tub', label: 'Hot tub' },
  { key: 'other', label: 'Other' },
];
const LOAD_SIZE_OPTIONS = [
  { key: 'small', label: 'Small — a few items' },
  { key: 'medium', label: 'Medium — about ¼ to ½ a truck' },
  { key: 'large', label: 'Large — about ½ to a full truck' },
  { key: 'xlarge', label: 'Extra large — multiple truckloads' },
];

const identity = v => v;

// A short, human title derived from what the visitor picked, so nobody is ever asked to write one.
// Kept ≤60 chars to match the listing-title maxLength enforced by EditListingDetailsForm.
const deriveJobTitle = values => {
  const junk = Array.isArray(values.junkType) ? values.junkType : [];
  if (junk.length) {
    const labels = junk.map(k => (JUNK_TYPE_OPTIONS.find(o => o.key === k) || {}).label || k);
    return labels
      .slice(0, 3)
      .join(', ')
      .slice(0, 60);
  }
  const svc = SERVICE_TYPE_OPTIONS.find(o => o.key === values.serviceType);
  return (svc && svc.label) || 'Junk removal job';
};

/**
 * Public, no-account "post your job" page (Haul.Taxi guest front door).
 *
 * The simplest path into the marketplace: a logged-out visitor adds photos + a pickup location,
 * passes a Turnstile challenge, enters an email, and a DRAFT job is created — no signup form and no
 * password. POST /api/guest-start creates the account + session behind the scenes; the draft is
 * prefilled with the photos, the optional job fields, and a derived title, and the visitor lands in
 * the post-a-job wizard to publish. From there the normal reverse-bid flow takes over (verified
 * haulers bid → customer picks). See GuestQuotePage.duck.js (startGuestPost).
 *
 * The pickup location the visitor types here IS carried into the draft, so the wizard's Location tab
 * arrives pre-filled instead of asking for the same address a second time. That write is
 * security-sensitive — street address and true coordinate to privateData, coarse city published,
 * geolocation fuzzed — so it is NOT reimplemented here: both this page and EditListingLocationPanel
 * call `buildPickupLocationUpdate` (util/listingLocation.js), which is the single owner of those
 * rules. If it cannot build a location (no geocoded place), we write none and the wizard asks.
 *
 * This page previously doubled as the AI front door and was gated on the AI feature flag — which
 * meant turning the AI off ALSO deleted the no-signup posting path and pushed everyone into the
 * signup-required wizard. The two are now independent: posting only needs Turnstile (which
 * guest-start requires anyway). The job is scoped later, on the wizard's photos step.
 *
 * @component
 */
export const GuestQuotePageComponent = props => {
  const { scrollingDisabled, onStartGuestPost, isAuthenticated } = props;
  const config = useConfiguration() || {};
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl();

  // Photos are kept as raw File objects in local state (previewed with ImageFromFile) and uploaded
  // to Sharetribe only once the job is actually posted — see GuestQuotePage.duck.js.
  const [photos, setPhotos] = useState([]);
  const [captchaToken, setCaptchaToken] = useState('');
  const [photoError, setPhotoError] = useState(null);
  const [postInProgress, setPostInProgress] = useState(false);
  const [postError, setPostError] = useState(null); // a translation id, or null
  const [emailExists, setEmailExists] = useState(false);
  const photoIdRef = useRef(0);
  const turnstileRef = useRef(null);

  // Fail closed on the sitekey: Turnstile's TEST sitekey issues no-challenge tokens and only
  // verifies against the TEST secret, so it must NEVER stand in for a missing prod key (that would
  // either break every real guest or void the gate). Use it only in development; in production an
  // unset REACT_APP_TURNSTILE_SITEKEY makes guest posting unavailable, mirroring the server, which
  // also fails closed when TURNSTILE_SECRET is unset.
  const dev = process.env.REACT_APP_ENV === 'development';
  const sitekey = process.env.REACT_APP_TURNSTILE_SITEKEY || (dev ? TURNSTILE_TEST_SITEKEY : null);
  const postingAvailable = !!sitekey;

  const title = intl.formatMessage({ id: 'GuestQuotePage.title' });
  const description = intl.formatMessage({ id: 'GuestQuotePage.description' });

  const handleAddPhotos = e => {
    const files = Array.from(e.target.files || []);
    // Allow re-selecting the same file after a remove.
    e.target.value = '';
    if (!files.length) {
      return;
    }
    // Enforce type + size + count at the picker so the visitor gets clear feedback here rather
    // than an opaque failure part-way through the upload that follows "post my job".
    const room = Math.max(0, MAX_PHOTOS - photos.length);
    const accepted = [];
    let rejected = 0;
    for (const file of files) {
      const isRoom = accepted.length < room;
      const isAllowed = ALLOWED_MEDIA_TYPES.includes(file.type) && file.size <= MAX_PHOTO_BYTES;
      if (isRoom && isAllowed) {
        photoIdRef.current += 1;
        accepted.push({ id: `${file.name}-${file.size}-${photoIdRef.current}`, file });
      } else {
        rejected += 1;
      }
    }
    setPhotoError(rejected > 0 ? 'GuestQuotePage.photoRejected' : null);
    if (accepted.length) {
      setPhotos(prev => [...prev, ...accepted]);
    }
  };

  const handleRemovePhoto = id => setPhotos(prev => prev.filter(p => p.id !== id));

  // "Post my job" — create the account + session (no signup form), then a prefilled draft, and land
  // the visitor in the existing post-a-job wizard. Needs the form `values` (fields collected above)
  // so it's invoked from the button inside the FinalForm render. This is now the ONLY consumer of
  // the single-use Turnstile token, so nothing else can spend it out from under this button.
  const handlePostJob = async values => {
    setPostError(null);
    setEmailExists(false);

    // Turnstile is required server-side ONLY to create the account (the anonymous path). On an
    // already-authenticated retry the thunk skips guest-start, so no token is needed. Gate here so
    // the anonymous path never fires a request the server would reject.
    if (!isAuthenticated && !captchaToken) {
      setPostError('GuestQuotePage.postCaptchaNeeded');
      return;
    }

    // The one Haul listing type supplies the process/unit for the draft (mirrors the wizard's
    // getTransactionInfo). Bail cleanly if the hosted config isn't loaded rather than posting junk.
    const listingTypeConfig = (config.listing?.listingTypes || [])[0];
    const transactionType = listingTypeConfig?.transactionType || {};
    if (!listingTypeConfig?.listingType || !transactionType.alias || !transactionType.unitType) {
      setPostError('GuestQuotePage.postError');
      return;
    }

    const junk = Array.isArray(values.junkType) ? values.junkType : [];

    // Carry the pickup address the visitor ALREADY typed and geocoded here into the draft, instead
    // of discarding it and asking again on the wizard's Location tab. buildPickupLocationUpdate is
    // the single writer of this marketplace's location-privacy model — street address and true
    // coordinate to privateData, coarse "City, ST" published, geolocation fuzzed ~500m — so this
    // path cannot drift from the wizard's. A malformed/ungeocoded place yields null and we simply
    // fall back to the old behaviour of asking again, rather than writing a half-formed location.
    const selectedPlace = values.location?.selectedPlace;
    const pickup = selectedPlace
      ? buildPickupLocationUpdate({
          address: selectedPlace.address,
          origin: selectedPlace.origin,
        })
      : null;

    const listingData = {
      title: deriveJobTitle(values),
      publicData: {
        listingType: listingTypeConfig.listingType,
        transactionProcessAlias: transactionType.alias,
        unitType: transactionType.unitType,
        ...(values.serviceType ? { serviceType: values.serviceType } : {}),
        ...(junk.length ? { junkType: junk } : {}),
        ...(values.loadSize ? { loadSize: values.loadSize } : {}),
        ...(pickup ? { location: pickup.publicLocation } : {}),
      },
      ...(pickup
        ? {
            privateData: { location: pickup.privateLocation },
            // No un-fuzzed fallback: `selectedPlace.origin` is the customer's exact doorstep and
            // geolocation is world-readable. `pickup` is non-null only when a fuzzed point exists,
            // so there is nothing to fall back to — and if that ever changes, writing no location
            // (the wizard then asks) must stay the failure mode.
            geolocation: new LatLng(pickup.fuzzed.lat, pickup.fuzzed.lng),
          }
        : {}),
    };

    setPostInProgress(true);
    try {
      const { id, slug } = await onStartGuestPost({
        email: values.email,
        captchaToken,
        photos,
        listingData,
      });
      // Land in the existing wizard on the freshly-created draft (prefilled from the quote).
      const path = createResourceLocatorString(
        'EditListingPage',
        routeConfiguration,
        { id, slug, type: 'draft', tab: 'details' },
        {}
      );
      history.push(path);
    } catch (e) {
      setPostInProgress(false);
      // A Turnstile token is single-use; whatever happened consumed it, so re-arm for any retry.
      setCaptchaToken('');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      if (e && e.code === EMAIL_EXISTS_CODE) {
        setEmailExists(true);
      } else {
        setPostError('GuestQuotePage.postError');
      }
    }
  };

  const addressRequiredMessage = intl.formatMessage({ id: 'GuestQuotePage.addressRequired' });
  const addressNotRecognizedMessage = intl.formatMessage({
    id: 'GuestQuotePage.addressNotRecognized',
  });

  const content = !postingAvailable ? (
    <div className={css.unavailable}>
      <Heading as="h1" rootClassName={css.heading}>
        <FormattedMessage id="GuestQuotePage.unavailableHeading" />
      </Heading>
      <p className={css.subheading}>
        <FormattedMessage id="GuestQuotePage.unavailableBody" />
      </p>
      <NamedLink name="NewListingPage" className={css.postJobCta}>
        <FormattedMessage id="GuestQuotePage.postJobCta" />
      </NamedLink>
    </div>
  ) : (
    <div className={css.content}>
      <header className={css.header}>
        <Heading as="h1" rootClassName={css.heading}>
          <FormattedMessage id="GuestQuotePage.heading" />
        </Heading>
        <p className={css.subheading}>
          <FormattedMessage id="GuestQuotePage.subheading" />
        </p>
      </header>

      <FinalForm
        onSubmit={() => {}}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, values, errors }) => (
          <Form
            className={css.form}
            onSubmit={e => {
              // Posting is triggered by the "Post my job" button (type="button"), so a stray submit
              // (e.g. Enter in a text field) is a deliberate no-op rather than an accidental post.
              e.preventDefault();
              handleSubmit(e);
            }}
          >
            <div className={css.field}>
              <label className={css.photosLabel} htmlFor="guestQuotePhotos">
                <FormattedMessage id="GuestQuotePage.photosLabel" />
              </label>
              <p className={css.photosHint}>
                <FormattedMessage id="GuestQuotePage.photosHint" values={{ max: MAX_PHOTOS }} />
              </p>
              {photos.length ? (
                <ul className={css.photoGrid}>
                  {photos.map(({ id, file }) => (
                    <li key={id} className={css.photoItem}>
                      <ImageFromFile id={id} file={file} aspectWidth={1} aspectHeight={1}>
                        <button
                          type="button"
                          className={css.removePhoto}
                          onClick={() => handleRemovePhoto(id)}
                        >
                          <FormattedMessage id="GuestQuotePage.removePhoto" />
                        </button>
                      </ImageFromFile>
                    </li>
                  ))}
                </ul>
              ) : null}
              {photos.length < MAX_PHOTOS ? (
                <label className={css.addPhoto} htmlFor="guestQuotePhotos">
                  <FormattedMessage id="GuestQuotePage.addPhoto" />
                  <input
                    id="guestQuotePhotos"
                    className={css.fileInput}
                    type="file"
                    accept={FILE_INPUT_ACCEPT}
                    multiple
                    onChange={handleAddPhotos}
                  />
                </label>
              ) : null}
              {photoError ? (
                <p className={css.photoError}>
                  <FormattedMessage
                    id={photoError}
                    values={{ max: MAX_PHOTOS, size: MAX_PHOTO_MB }}
                  />
                </p>
              ) : null}
            </div>

            <FieldLocationAutocompleteInput
              className={css.field}
              name="location"
              id="guestQuoteLocation"
              label={intl.formatMessage({ id: 'GuestQuotePage.addressLabel' })}
              placeholder={intl.formatMessage({ id: 'GuestQuotePage.addressPlaceholder' })}
              useDefaultPredictions={false}
              format={identity}
              valueFromForm={values.location}
              validate={validators.composeValidators(
                validators.autocompleteSearchRequired(addressRequiredMessage),
                validators.autocompletePlaceSelected(addressNotRecognizedMessage)
              )}
            />

            {/* serviceType / junkType / loadSize are optional — photos + an address are all that is
                ever required. They just help haulers judge the job, and seed the listing's fields
                (and its derived title) so nobody has to write one. */}
            <FieldSelect
              className={css.field}
              id="guestQuoteServiceType"
              name="serviceType"
              label={intl.formatMessage({ id: 'GuestQuotePage.serviceTypeLabel' })}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'GuestQuotePage.serviceTypePlaceholder' })}
              </option>
              {SERVICE_TYPE_OPTIONS.map(({ key, label }) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </FieldSelect>

            <FieldCheckboxGroup
              className={css.field}
              id="guestQuoteJunkType"
              name="junkType"
              label={intl.formatMessage({ id: 'GuestQuotePage.junkTypeLabel' })}
              options={JUNK_TYPE_OPTIONS}
              twoColumns
            />

            <FieldSelect
              className={css.field}
              id="guestQuoteLoadSize"
              name="loadSize"
              label={intl.formatMessage({ id: 'GuestQuotePage.loadSizeLabel' })}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'GuestQuotePage.loadSizePlaceholder' })}
              </option>
              {LOAD_SIZE_OPTIONS.map(({ key, label }) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </FieldSelect>

            <div className={css.field}>
              <span className={css.captchaLabel}>
                <FormattedMessage id="GuestQuotePage.captchaLabel" />
              </span>
              <Turnstile
                ref={turnstileRef}
                siteKey={sitekey}
                onSuccess={token => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken('')}
                onError={() => setCaptchaToken('')}
              />
            </div>

            <div className={css.postSection}>
              <Heading as="h2" rootClassName={css.postHeading}>
                <FormattedMessage id="GuestQuotePage.postHeading" />
              </Heading>
              <p className={css.postSubheading}>
                <FormattedMessage id="GuestQuotePage.postSubheading" />
              </p>

              <FieldTextInput
                id="guestQuoteEmail"
                name="email"
                type="email"
                className={css.field}
                label={intl.formatMessage({ id: 'GuestQuotePage.emailLabel' })}
                placeholder={intl.formatMessage({ id: 'GuestQuotePage.emailPlaceholder' })}
                validate={validators.composeValidators(
                  validators.required(intl.formatMessage({ id: 'GuestQuotePage.emailRequired' })),
                  validators.emailFormatValid(
                    intl.formatMessage({ id: 'GuestQuotePage.emailInvalid' })
                  )
                )}
              />

              {/* This button is type="button" and calls handlePostJob directly, so Final Form's
                  submit-time validation NEVER runs — every requirement has to be repeated here or
                  it isn't enforced at all. Without the photo/address checks a visitor could create
                  an account and an empty, unpublishable draft with neither. Photos and address are
                  the only two things a customer is ever required to provide, so they gate this for
                  authenticated visitors too, not just anonymous ones. */}
              <PrimaryButton
                type="button"
                className={css.postButton}
                inProgress={postInProgress}
                disabled={
                  postInProgress ||
                  photos.length === 0 ||
                  !!errors.location ||
                  (!isAuthenticated && (!!errors.email || !captchaToken))
                }
                onClick={() => handlePostJob(values)}
              >
                <FormattedMessage id="GuestQuotePage.postButton" />
              </PrimaryButton>

              {emailExists ? (
                <p className={css.postError}>
                  <FormattedMessage id="GuestQuotePage.emailExists" />{' '}
                  <NamedLink name="LoginPage" className={css.signInLink}>
                    <FormattedMessage id="GuestQuotePage.signInLink" />
                  </NamedLink>
                </p>
              ) : null}
              {postError ? (
                <p className={css.postError}>
                  <FormattedMessage id={postError} />
                </p>
              ) : null}
            </div>
          </Form>
        )}
      />
    </div>
  );

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        mainColumnClassName={css.layoutMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {content}
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
});

const mapDispatchToProps = dispatch => ({
  onStartGuestPost: params => dispatch(startGuestPost(params)),
});

const GuestQuotePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(GuestQuotePageComponent);

export default GuestQuotePage;
